export enum ESalesMetricsGroupBy {
  CREATION_DATE = 'creation',
  ARRIVAL_DATE = 'arrival',
}

export enum EDashboardBookingStatus {
  BASKET = 'basket',
  ENQUIRY = 'enquiry',
  REQUESTED = 'requested',
  CONFIRMED = 'confirmed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  DISCARDED = 'discarded',
}

export interface IBookingsByDestinationRequest {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  salesUserUuid?: string;
  dateType?: ESalesMetricsGroupBy;
  status?: EDashboardBookingStatus[];
  companyCountry?: string[];
  company?: string[];
  destination?: string[];
  hotel?: string[];
}

export interface IDestinationEntry extends Record<string, unknown> {
  countryCode: string;
  countryName: string;
  bookingCount: number;
  percentage: number;
}

export interface IDestinationBreakdownResponse {
  /** Period start date (YYYY-MM-DD) */
  periodStart: string;
  /** Period end date (YYYY-MM-DD) */
  periodEnd: string;
  totalBookings: number;
  destinations: IDestinationEntry[];
  lastUpdated: string;
}

export interface IDNameFilterOption {
  id: string;
  name: string;
}

export interface TravelCompanyFilterOption {
  /** Format: uuid */
  id: string;
  name: string;
  countryId: string;
}

export interface HotelFilterOption {
  /** Format: uuid */
  id: string;
  name: string;
  destinationId: string;
}

export interface ISalesMetricsFiltersRequest {
  salesUserUuid: string;
}

export interface ISalesMetricsFiltersResponse {
  travelCompanyCountries: IDNameFilterOption[];
  travelCompanies: TravelCompanyFilterOption[];
  destinations: IDNameFilterOption[];
  hotels: HotelFilterOption[];
  statuses: IDNameFilterOption[];
  /** Format: date-time */
  lastUpdated: string;
}

export interface ISalesMetricsFilters {
  dateType?: ESalesMetricsGroupBy;
  status: EDashboardBookingStatus[];
  companyCountry: string[];
  company: string[];
  destination: string[];
  hotel: string[];
}

export interface ISalesMetricsRequest extends ISalesMetricsFilters {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  salesUserUuid: string;
}

export interface ISalesMetricsResponse {
  summary: {
    totalBookings: number;
    totalValueCents: number;
    bookingsChangePercent: number;
    valueChangePercent: number;
  };
  byMonth: ISalesMetricsByMonth[];
  lastUpdated: string;
}

export interface ISalesMetricsByMonth {
  year: number;
  month: number;
  bookingCount: number;
  bookingValueCents: number;
  previousYearBookingCount: number;
  previousYearBookingValueCents: number;
}

// Top Performers API Types

export type PerformerCategory = 'TA' | 'TC' | 'HOTEL';

export type TopPerformersSortBy = 'count' | 'value';

export type TopPerformersSortDirection = 'desc' | 'asc';

/** Request body for POST /api/v1/top-performers */
export interface ITopPerformersRequest {
  startYear: number;
  startMonth: number;
  endYear: number;
  endMonth: number;
  /** Which category to fetch. Default: 'all' */
  category?: PerformerCategory | 'all';
  /** Sort by booking count or value */
  sortBy?: TopPerformersSortBy;
  /** Sort direction */
  sortDirection?: TopPerformersSortDirection;
  /** Number of results per category (1-10) */
  limit?: number;
  /** Group by creation or arrival date */
  dateType?: ESalesMetricsGroupBy;
  /** Filter by booking status(es) */
  status?: EDashboardBookingStatus[];
  /** Filter by Travel Company country */
  companyCountry?: string[];
  /** Filter by Travel Company (be_id) */
  company?: string[];
  /** Filter by destination country */
  destination?: string[];
  /** Filter by hotel */
  hotel?: string[];
  /** Filter by sales user UUID (admin only) */
  salesUserUuid?: string;
}

/** Single performer entry in the response */
export interface IPerformerEntry {
  /** 1-based position in list */
  rank: number;
  /** Unique identifier */
  uuid: string;
  /** Display name */
  name: string;
  /** ISO country code (e.g., "US", "GB", "MV") */
  countryCode: string;
  /** Total bookings in period */
  bookingCount: number;
  /** Total sales value in cents (divide by 100 for display) */
  bookingValueCents: number;
}

/** Response from POST /api/v1/top-performers */
export interface ITopPerformersResponse {
  /** Period start date (YYYY-MM-DD) */
  periodStart: string;
  /** Period end date (YYYY-MM-DD) */
  periodEnd: string;
  /** What the results are sorted by */
  sortedBy: TopPerformersSortBy;
  /** Sort direction of results */
  sortDirection: TopPerformersSortDirection;
  /** Top Travel Agents (empty array if not requested) */
  topTAs: IPerformerEntry[];
  /** Top Travel Companies (empty array if not requested) */
  topTCs: IPerformerEntry[];
  /** Top Hotels (empty array if not requested) */
  topHotels: IPerformerEntry[];
  /** Total matching TAs (0-10, independent of limit) */
  totalTAs: number;
  /** Total matching TCs (0-10, independent of limit) */
  totalTCs: number;
  /** Total matching Hotels (0-10, independent of limit) */
  totalHotels: number;
  /** Format: ISO date-time */
  lastUpdated: string;
}

/** Config for each individual table (sort/limit can differ per table) */
export interface ITableConfig {
  sortBy: TopPerformersSortBy;
  sortDirection: TopPerformersSortDirection;
  /** 5 = collapsed, 10 = expanded */
  limit: 5 | 10;
}

/** Combined state holding all 3 tables' data + their individual configs */
export interface ITopPerformersState {
  topTAs: IPerformerEntry[];
  topTCs: IPerformerEntry[];
  topHotels: IPerformerEntry[];
  tableConfigs: Record<PerformerCategory, ITableConfig>;
  periodStart: string;
  periodEnd: string;
  lastUpdated: string;
}

/** Default config for all tables */
export const DEFAULT_TABLE_CONFIG: ITableConfig = {
  sortBy: 'value',
  sortDirection: 'desc',
  limit: 5,
};

// TA Metrics API Types

export interface ITaMetricsRequest {
  startYear?: number;
  startMonth?: number;
  endYear?: number;
  endMonth?: number;
  /** Filter by sales user UUID (admin only) */
  salesUserUuid?: string;
}

export interface ITaMetricsByMonth {
  year: number;
  month: number;
  activeTAs: number;
  loggedInTAs: number;
  newTAs: number;
}

export interface ITaMetricsResponse {
  /** Period start date (YYYY-MM-DD) */
  periodStart: string;
  /** Period end date (YYYY-MM-DD) */
  periodEnd: string;
  byMonth?: ITaMetricsByMonth[];
}

// CRM Metrics API Types

export interface ICrmMetricsRequest {
  startYear?: number;
  startMonth?: number;
  endYear?: number;
  endMonth?: number;
  /** Filter by sales user UUID (admin only) */
  salesUserUuid?: string;
}

export interface IActiveLeads {
  discovery: number;
  nurture: number;
  confirmed: number;
  total: number;
}

export interface IWinLossRates {
  periodStart: string;
  periodEnd: string;
  wins: number;
  losses: number;
  winRate: number;
  lossRate: number;
}

export interface IPipelineStage {
  stage: 'DISCOVERY' | 'NURTURE' | 'CONFIRMED';
  count: number;
  percentage: number;
}

export interface IConversionRates {
  discoveryToNurture: number;
  nurtureToConfirmed: number;
}

export interface IPipeline {
  stages: IPipelineStage[];
  conversionRates: IConversionRates;
}

export interface ICrmMetricsResponse {
  activeLeads: IActiveLeads;
  converted: number;
  winLossRates: IWinLossRates;
  pipeline: IPipeline;
  lastUpdated: string;
}
